import React, { useEffect, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';

import Select from 'components/partials/select/select';

import { useGoalForm } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/context/context';

import useGenerateFields from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-form/hooks/useGenerateFields';
import type { FieldConfig } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-form/hooks/useGenerateFields';
import useHandleSelectChange from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-form/hooks/useHandleSelectChange';

import { schema } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-configuration/goals-configuration';

import type { CategoryOption } from 'containers/admin/clients/client/tabs-components/goals-tab/types';

interface DetailFieldsProps {
  detailIndex: number;
  detailData: Record<string, unknown>;
  category: string;
  isDisabled: boolean;
  type: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const DetailFields = ({
  detailIndex,
  detailData,
  category,
  isDisabled,
  type,
}: DetailFieldsProps): React.JSX.Element => {
  const { resetField, clearErrors, loading, setValue, control } = useGoalForm();

  const [fields, setFields] = useState<FieldConfig[]>([]);
  const [renderKey, setRenderKey] = useState<number>(0);

  const initializeFormValues = useGenerateFields(schema, type);

  const handleSelectChange = useHandleSelectChange(
    fields,
    setFields,
    setValue,
    resetField
  );

  const watchedDetail = useWatch({
    control,
    name: `details.${detailIndex}`,
  }) as Record<string, unknown> | undefined;

  useEffect(() => {
    if (loading || !category || fields.length) return;

    const categoryConfig = schema[type]?.categories.find(
      (cat) => cat.name === category
    );

    if (categoryConfig) {
      const initializedFields =
        detailData && Object.keys(detailData).length
          ? initializeFormValues(categoryConfig.details, detailData)
          : categoryConfig.details
          ? [categoryConfig.details]
          : [];

      setFields(initializedFields);
    }
  }, [category, type, loading, detailIndex, detailData, fields.length]);

  useEffect(() => {
    if (!fields.length || loading) return;

    fields.forEach((fieldConfig, index) => {
      if (!fieldConfig) return;

      const options = fieldConfig.options || [];
      const currentValue = fieldConfig.associatedPropertyName
        ? watchedDetail?.[fieldConfig.associatedPropertyName]
        : undefined;

      if (options.length === 1 && currentValue !== options[0].value) {
        const singleOption = options[0].value;

        setValue(
          `details.${detailIndex}.${fieldConfig.associatedPropertyName}`,
          singleOption
        );

        handleSelectChange({
          value: singleOption,
          field: fieldConfig,
          onChange: () => {},
          index,
          detailIndex,
        });
      }
    });
  }, [
    fields,
    detailIndex,
    loading,
    watchedDetail,
    setValue,
    handleSelectChange,
  ]);

  useEffect(() => {
    setRenderKey((prevKey) => prevKey + 1);
  }, [fields]);

  const getSelectValue = (
    options: CategoryOption[],
    value: unknown,
    multiple?: boolean
  ): CategoryOption | CategoryOption[] | undefined =>
    multiple
      ? options.filter((option) =>
          (value as unknown[] | undefined)?.includes(option.value)
        )
      : options.find((option) => option.value === value);

  interface HandleOnChangeArgs {
    selectedOption: SelectOption | SelectOption[] | null;
    multiple?: boolean;
    onChange: (value: unknown) => void;
    field: FieldConfig;
    index: number;
    detailIndex: number;
  }

  const handleOnChange = ({
    selectedOption,
    multiple,
    onChange,
    field,
    index,
    detailIndex,
  }: HandleOnChangeArgs): void => {
    const newValue = multiple
      ? (selectedOption as SelectOption[] | null)?.map(
          (option) => option.value
        ) ?? []
      : (selectedOption as SelectOption | null)?.value ?? '';

    handleSelectChange({
      value: newValue,
      field,
      onChange,
      index,
      detailIndex,
    });
  };

  const renderOptions = (options: CategoryOption[]): SelectOption[] =>
    options.map((option) => ({ value: option.value, label: option.label }));

  const renderField = (
    field: FieldConfig | null,
    index: number
  ): React.JSX.Element | null => {
    if (!field) return null;

    const {
      associatedPropertyName,
      displayName,
      options = [],
      required,
      multiple,
    } = field;
    const singleOption = options.length === 1 ? options[0] : null;

    return (
      <div
        key={`${associatedPropertyName}-${detailIndex}-${renderKey}`}
        className="shrink-0"
      >
        <Controller
          name={`details.${detailIndex}.${associatedPropertyName}`}
          control={control}
          rules={{ required }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              value={getSelectValue(options, value, multiple)}
              onChange={(
                selectedOption: SelectOption | SelectOption[] | null
              ) =>
                handleOnChange({
                  selectedOption,
                  multiple,
                  onChange,
                  field,
                  index,
                  detailIndex,
                })
              }
              options={renderOptions(options)}
              disabled={isDisabled || Boolean(singleOption)}
              selectWrapperClass="!min-w-[150px]"
              labelInTop={true}
              label={displayName}
              isMulti={multiple}
              fieldError={
                error && { ...error, message: 'This field is required' }
              }
              clearInputErr={() =>
                clearErrors(`details.${detailIndex}.${associatedPropertyName}`)
              }
              isClearable={!required}
              required={required}
            />
          )}
        />
      </div>
    );
  };

  return (
    <div className="flex gap-3 flex-wrap">
      {fields.map((field, index) => renderField(field, index))}
    </div>
  );
};

export default React.memo(DetailFields);
